import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import PrichardCaseStudyHero from "@/components/home/PrichardCaseStudyHero";
import JourneyNav from "@/components/home/JourneyNav";
import CanvassStage from "@/components/home/stages/CanvassStage";
import PlanStage from "@/components/home/stages/PlanStage";
import FundStage from "@/components/home/stages/FundStage";
import GrowStage from "@/components/home/stages/GrowStage";

const interestSchema = z.object({
  name: z.string().trim().min(1, "Your name is required").max(200),
  email: z.string().trim().email("Enter a valid email").max(320),
  organization: z.string().trim().max(200).optional().or(z.literal("")),
});

type InterestForm = z.infer<typeof interestSchema>;

const ComingSoon = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<InterestForm>({
    resolver: zodResolver(interestSchema),
    defaultValues: { name: "", email: "", organization: "" },
  });

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "The Emerging Market — Private Beta";
    return () => {
      document.title = prevTitle;
    };
  }, []);

  const onSubmit = async (values: InterestForm) => {
    const { error } = await supabase.from("signups").insert({
      name: values.name.trim(),
      email: values.email.trim().toLowerCase(),
      organization: values.organization?.trim() || null,
    });
    if (error) {
      toast.error("Something went wrong. Please try again.");
      return;
    }
    setSubmitted(true);
    form.reset();
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader variant="hero" />
      <main id="main">
        <PrichardCaseStudyHero />
        <JourneyNav />
        <CanvassStage />
        <PlanStage />
        <FundStage />
        <GrowStage />

        {/* Unauth closing CTA — opens Request Access dialog */}
        <section className="bg-hero py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="text-hero-muted text-xs font-semibold tracking-[0.3em] uppercase mb-5">
              Join the beta
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-hero-fg leading-tight mb-5">
              Bring your idea. We'll bring the tools and the room.
            </h2>
            <p className="text-hero-muted text-base md:text-lg max-w-xl mx-auto mb-6 leading-relaxed">
              The Emerging Market is an Alabama public benefit corporation (LLC) in private beta. We build strategies for grassroots organizations and businesses across the state.
            </p>
            <p className="text-hero-muted text-sm max-w-xl mx-auto mb-8 leading-relaxed">
              Request access and we'll loop you in as seats open for your group.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button
                size="lg"
                onClick={() => setDialogOpen(true)}
                className="bg-accent text-accent-foreground hover:bg-accent/90 font-display font-semibold text-base px-7 h-12 rounded-full shadow-glow"
              >
                Request access <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/25 bg-white/5 text-hero-fg hover:bg-white/10 hover:text-hero-fg font-display font-semibold text-base px-6 h-12 rounded-full"
              >
                <Link to="/auth">Member sign in</Link>
              </Button>
            </div>
          </motion.div>
          </div>
        </section>
      </main>

      <SiteFooter />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Request early access
            </DialogTitle>
            <DialogDescription>
              Tell us who you are and we'll loop you in as the beta opens to
              your stakeholder group.
            </DialogDescription>
          </DialogHeader>

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-4 text-center"
            >
              <h3 className="font-display text-lg font-semibold">
                You're on the list.
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Thanks for raising your hand. We'll be in touch.{" "}
                <Link to="/auth" className="text-primary hover:underline">
                  Already invited? Sign in.
                </Link>
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-4"
                onClick={() => setSubmitted(false)}
              >
                Submit another
              </Button>
            </motion.div>
          ) : (
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
              noValidate
            >
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  autoComplete="name"
                  placeholder="Jane Doe"
                  {...form.register("name")}
                />
                {form.formState.errors.name && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.name.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@organization.org"
                  {...form.register("email")}
                />
                {form.formState.errors.email && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.email.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="organization">
                  Organization{" "}
                  <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="organization"
                  autoComplete="organization"
                  placeholder="Your organization, business, agency, or firm"
                  {...form.register("organization")}
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Request access
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
              <p className="text-center text-[11px] text-muted-foreground">
                We'll only use your details to coordinate beta access.
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComingSoon;